// ---- Talk / dialogue (client) ----
// Conversations layered on top of the focus system, the peaceful sibling of
// combat.ts. The SERVER coordinates each conversation (it's exclusive per NPC):
// requesting a talk asks for a lock; the server grants it only if the NPC is free
// and we're in range, then halts the NPC's roam and turns it to face us (see
// index.ts). This module is the client half: it sends the talk *intents*
// (`talk`/`talkEnd`), confirms the conversation started by watching the snapshot's
// `talker` field (written onto each Live by net.ts), drives the bottom action-bar
// Talk slot, and renders the branching dialogue HUD.
//
// A talkable NPC (one whose spec has a `talk` block) offers a Talk action in the
// action bar either when focused OR when you walk into talk range (proximity soft-
// target — no click needed). Soft-target is pure client presentation: it never
// locks the camera or engages combat; clicking Talk still sends the same server
// intent. Starting a conversation locks movement (game.talking) and swings the
// camera into a side-on two-shot (driven from game.ts via camera.ts
// `updateTalkCamera`). The conversation is a node graph: the NPC speaks, you pick
// a response, which routes to another node and/or fires an optional `action` hook
// (startQuest/advanceQuest/openShop/giveItem are live server-validated intents;
// custom remains a log stub — see applyTalkAction). An explicit "Farewell"
// choice (a choice with no `to`) or Esc ends it, and ending a conversation DROPS
// focus entirely (endTalk → clearFocus): the ring/banner/action bar close.

import { game, inputLocked } from "./state.ts";
import { getFocus, clearFocus } from "./focus.ts";
import { npcs, type Live } from "./npc/index.ts";
import { talkNode, resolveTalkRoot, type TalkAction } from "./npc/define.ts";
import { me, sendTalk, sendTalkEnd, sendQuestStart, sendQuestAdvance } from "./net.ts";
import { condMet } from "./quest.ts";
import { openShop, closeShop } from "./shop.ts";
import { openGift, closeGift } from "./gift.ts";
import { requestCinematic } from "./cinematic/index.ts";
import { reachDist } from "./shared.ts";
import type { ItemType } from "./shared.ts";
import { resetTalkCamera, talkFollowYaw } from "./camera.ts";
import { chatTyping } from "./chat.ts";
import { refreshCoach } from "./coach.ts";

let active = false;            // a conversation is open right now
let pending: Live | null = null; // talk requested, awaiting the server's grant
let pendingT = 0;              // seconds we've waited on the current request
const PENDING_TIMEOUT = 1.5;  // give up on an unconfirmed request after this
const TALK_EXIT_DUR = 0.5;    // camera ease-back window after a conversation ends
let current: Live | null = null; // the NPC we're talking to (while active)
let nodeId = "";               // current conversation node id
let renderedNode = "";         // last node id rendered (so we don't rebuild every frame)

let dialogueEl: HTMLElement | null = null;
let nameEl: HTMLElement | null = null;
let lineEl: HTMLElement | null = null;
let choicesEl: HTMLElement | null = null;

/** Distance from the local player to an NPC on the xz plane — Infinity when
 *  a floor apart (the vertical reach gate, gravity plan), so every range
 *  comparison downstream inherits it. */
function dist(L: Live): number {
  const p = game.player.position, q = L.group.position;
  return reachDist(p.x, game.vert.y, p.z, q.x, L.feetY, q.z);
}

/** The conversation range for an NPC (talk.range, else its proximity radius, else 6). */
function talkRange(L: Live): number {
  return L.spec.talk?.range ?? L.spec.radius ?? 6;
}

/** True while someone OTHER than us holds this NPC's conversation lock. */
function busyByOther(L: Live): boolean {
  return !!L.talker && L.talker !== me.id;
}

/** True when another activity owns the action bar / camera (KO, aim, fish, …).
 *  Talk is exempted while mid-conversation so the slot can stay disabled-visible. */
function talkBlocked(): boolean {
  return game.down || game.journalOpen || game.fishing || game.catchOpen
    || game.aiming || game.cooking || game.transferring || game.cinematic;
}

/**
 * Who the Talk slot is about right now:
 *   1. The focused NPC, if talkable (even out of range → slot shows disabled)
 *   2. Else the nearest talkable NPC inside their talk range (proximity soft-target)
 *
 * Soft-target never sets focus / never engages combat — pure HUD presentation so
 * walking up to Dekkar (or any talker) pops the Talk button without a click.
 */
function talkCandidate(): Live | null {
  const f = getFocus() as Live | null;
  if (f && f.talkable && !f.defeated && !f.netHidden) return f;

  let best: Live | null = null;
  let bestD = Infinity;
  for (const L of npcs()) {
    if (!L.talkable || L.defeated || L.netHidden) continue;
    const d = dist(L);
    if (d > talkRange(L)) continue;
    if (d < bestD) { bestD = d; best = L; }
  }
  return best;
}

export function initTalk(): void {
  dialogueEl = document.getElementById("dialogue");
  nameEl = dialogueEl?.querySelector(".talk-name") as HTMLElement | null;
  lineEl = dialogueEl?.querySelector(".talk-line") as HTMLElement | null;
  choicesEl = dialogueEl?.querySelector(".talk-choices") as HTMLElement | null;

  document.getElementById("talkBtn")?.addEventListener("click", requestTalk);

  // Esc ends a conversation; number keys 1–9 pick the matching choice. focus.ts's
  // Esc handler is registered first and no-ops while talking, so this wins cleanly.
  // T-to-talk is handled via tryTalkHotkey() from input.ts so it can yield the
  // key to hold-T world-clock FF when Talk isn't available.
  addEventListener("keydown", (e) => {
    if (!active) return;
    if (e.key === "Escape") { endTalk(); return; }
    if (e.key >= "1" && e.key <= "9") choose(Number(e.key) - 1);
  });
}

/** Press-edge "T" hotkey: start a talk when the Talk slot is enabled. Returns
 *  true when the key was consumed so input.ts can skip hold-T time-fast-forward.
 *  Mid-conversation T is also consumed (no FF under dialogue) without re-firing. */
export function tryTalkHotkey(): boolean {
  if (chatTyping()) return false;
  if (active || pending) return true; // swallow T while talking / waiting on grant
  if (!talkSlotState().enabled) return false;
  requestTalk();
  return true;
}

/** Action-bar Talk slot state for combat.ts to render (it owns the bar). */
export function talkSlotState(): { show: boolean; enabled: boolean; label: string } {
  if (talkBlocked()) return { show: false, enabled: false, label: "Talk" };
  const t = talkCandidate();
  if (!t) return { show: false, enabled: false, label: "Talk" };
  if (busyByOther(t)) return { show: true, enabled: false, label: "Busy" };
  const inRange = dist(t) <= talkRange(t);
  const enabled = !active && !pending && inRange;
  return { show: true, enabled, label: "Talk" };
}

/** Wired to the Talk action-bar button: request the conversation lock from the
 *  server if the candidate (focused or proximity soft-target) is talkable, in
 *  range, and free. */
function requestTalk(): void {
  if (active || pending || talkBlocked() || inputLocked()) return;
  const t = talkCandidate();
  if (!t || !t.talkable || t.defeated) return;
  if (busyByOther(t) || dist(t) > talkRange(t)) return;
  pending = t;
  pendingT = 0;
  sendTalk(t.spec.id);
}

/** Per-frame: resolve a pending request once the server grants/refuses it, then
 *  (while active) keep the HUD in sync. The cutscene camera is driven separately
 *  from game.ts (camera.ts `updateTalkCamera`). */
export function updateTalk(dt: number): void {
  // Awaiting the server's grant: activate when WE hold the lock; bail if someone
  // else got it first, the NPC died, we walked away / focused something else, or
  // the grant never arrives (a refused request — e.g. a position desync on the
  // server's range check). Soft-target talks don't require focus, so "walked
  // away" is the range check rather than a focus identity match.
  if (pending) {
    pendingT += dt;
    const focusedElse = (() => {
      const f = getFocus();
      return !!f && f !== pending;
    })();
    const outOfReach = dist(pending) > talkRange(pending) * 1.5;
    if (pending.defeated || busyByOther(pending) || focusedElse || outOfReach || pendingT > PENDING_TIMEOUT) {
      if (pending.talker === me.id) sendTalkEnd(pending.spec.id);
      pending = null;
    } else if (pending.talker === me.id) {
      activate(pending);
    }
    // else: still waiting for the snapshot to confirm — hold.
  }

  if (!active || !current) return;

  // Safety: the server can drop our lock (we strayed out of range, disconnected,
  // or the NPC was defeated). If it's no longer ours, close locally. `netHidden`
  // covers the partner leaving our interest area — shouldn't happen mid-talk
  // (the talk lock drops at talkRange, far inside the interest radius), but a
  // teleport/displacement could.
  if (current.defeated || current.talker !== me.id || current.netHidden) { endTalk(); return; }

  if (nodeId !== renderedNode) renderNode();
}

function activate(L: Live): void {
  pending = null;
  active = true;
  current = L;
  game.talking = true;
  // Step-gated entry nodes open first ("Got my logs yet?") when their `when`
  // matches; otherwise fall back to talk.root (generic greeting).
  nodeId = resolveTalkRoot(L.spec.talk!, condMet);
  renderedNode = "";
  if (L.label) L.label.visible = false; // drop the floating name tag while conversing
  resetTalkCamera(); // ease the two-shot in from the live gameplay view
  renderNode();
}

/** A node's choices with any whose `when` quest-condition isn't met filtered out, so
 *  one conversation can branch on quest state. renderNode + choose use this same list
 *  so number keys / clicks map to the visible buttons. */
function visibleChoices(node: { choices?: import("./npc/define.ts").TalkChoice[] }) {
  return (node.choices ?? []).filter((c) => !c.when || condMet(c.when));
}

function renderNode(): void {
  if (!current || !dialogueEl) return;
  const talk = current.spec.talk!;
  const node = talkNode(talk, nodeId);
  if (!node) { endTalk(); return; } // dangling id → just close

  if (nameEl) nameEl.textContent = current.spec.name;
  if (lineEl) lineEl.textContent = Array.isArray(node.npc) ? node.npc.join("\n\n") : node.npc;

  if (choicesEl) {
    choicesEl.replaceChildren();
    const choices = visibleChoices(node);
    if (choices.length === 0) {
      // A node with no choices is a dead-end line — offer a single Continue→end.
      choicesEl.appendChild(choiceButton("Continue", 1, () => endTalk()));
    } else {
      choices.forEach((c, i) => {
        choicesEl!.appendChild(choiceButton(c.text, i + 1, () => choose(i)));
      });
    }
  }

  dialogueEl.style.display = "flex";
  renderedNode = nodeId;
}

function choiceButton(text: string, num: number, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.className = "talk-choice";
  b.type = "button";
  b.innerHTML = `<span class="talk-key">${num}</span>${text}`;
  b.addEventListener("click", onClick);
  return b;
}

/** Pick the i-th response of the current node: fire its action hook, then route to
 *  its target node (or end the conversation if it has none). */
function choose(i: number): void {
  if (!active || !current) return;
  const talk = current.spec.talk!;
  const node = talkNode(talk, nodeId);
  const choice = node ? visibleChoices(node)[i] : undefined;
  if (!choice) return;
  if (choice.action) applyTalkAction(choice.action);
  if (choice.to && talkNode(talk, choice.to)) {
    nodeId = choice.to;
    renderNode(); // advance immediately for snappy feel
    // Keepalive: re-send the talk intent so the server refreshes the lock's
    // idle-release clock (npc-ai TALK_IDLE_MS) — the handler treats a re-request
    // by the current talker as a refresh, so a long branching conversation is
    // never dropped mid-read. Node traversal is otherwise client-only.
    sendTalk(current.spec.id);
  } else {
    endTalk();
  }
}

/** Dispatch a choice's side effect. Quests/gifts/shops are server-authoritative:
 *  we send the intent (or open a conversation-gated panel); the server validates
 *  and echoes truth. `custom` is still a log stub. */
function applyTalkAction(a: TalkAction): void {
  if (a.kind === "startQuest") { sendQuestStart(a.id); return; }
  if (a.kind === "advanceQuest") { sendQuestAdvance(a.id); return; }
  // Open the vendor panel over the (still-active) conversation — the lock we hold is
  // what authorizes buy/sell server-side. The choice routes back to a talk node, so
  // closing the shop drops you back into the conversation.
  if (a.kind === "openShop") { openShop(a.id); return; }
  // Gift hand-off (reusable): open the gift modal over the conversation. The
  // choice usually also routes to the post-gift node; Take it grants server-side
  // and auto-closes the panel so dialogue continues.
  if (a.kind === "giveItem") {
    openGift(a.type as ItemType, current?.spec.name);
    return;
  }
  // Launch a cutscene (plan 26): end the conversation first (the cinematic swaps
  // the world out — the talk lock must release), then hand off to game.ts's
  // registered launcher (the there-and-back stage flow lives there).
  if (a.kind === "playCinematic") { endTalk(); requestCinematic(a.id); return; }
  // eslint-disable-next-line no-console
  console.log(`[talk] action: ${a.kind}`, a);
}

function endTalk(): void {
  closeShop(); // the shop rides on top of the conversation lock — close it with the talk
  closeGift(); // same for the gift hand-off panel
  if (current) {
    sendTalkEnd(current.spec.id);
    if (current.label) current.label.visible = true; // restore the name tag
  }
  active = false;
  current = null;
  pending = null;
  game.talking = false;
  game.talkExit = TALK_EXIT_DUR; // ease the camera back from the two-shot (game.ts)
  // Continue the follow yaw from where the two-shot (incl. any Q/E orbit) left the
  // view, so the exit ease doesn't swing a big orbit back to a fixed heading.
  game.camYaw = talkFollowYaw(game.camYaw);
  if (dialogueEl) dialogueEl.style.display = "none";
  clearFocus(); // leaving the conversation also drops the target (ring/banner/bar)
  // Tutorial coach was suppressed mid-talk — re-evaluate now (bag glow, etc.).
  refreshCoach();
}

/** Map swap (plan 24, game.ts swapWorld): drop any conversation without
 *  ceremony. The server already released the talk lock in transferCharacter;
 *  the NPC the dialogue referenced is about to be torn down, and the camera
 *  must not ease back across two worlds (the swap cuts through a fade). */
export function forceEndTalk(): void {
  if (active || pending || current) endTalk();
  game.talkExit = 0;
}
